import React, { useEffect, useMemo, useState } from 'react';
import { Plus, Trash2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import API_URL from '@/config/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { formatApiError } from '@/utils/apiError';
import { BRAND } from '@/pages/trainingBrand';
import { StockPicker, clearStockCache, stockLineCost } from '@/components/sales/StockPicker';
import { SupplierManager, fetchSuppliers } from '@/components/sales/SupplierManager';
import { PaymentMethodManager, fetchPaymentMethods } from '@/components/sales/PaymentMethodManager';

export const STOCK_SUPPLIER_ID = 'sup_stock';

export const SALES_BRANDS = [
  { key: 'probound', label: 'ProBound', color: BRAND.probound.primary, ink: BRAND.probound.ink },
  { key: 'proepoxy', label: 'ProEpoxy', color: BRAND.proepoxy.primary, ink: BRAND.proepoxy.ink },
  { key: 'prodecor', label: 'ProDecor', color: BRAND.prodecor.primary, ink: BRAND.prodecor.ink },
  { key: 'other', label: 'Other', color: '#4b5563', ink: '#fff' },
];
export const gbp = (n) => { const v = Number(n || 0); const s = `£${Math.abs(v).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`; return v < 0 ? `−${s}` : s; };
const todayIso = () => new Date().toISOString().slice(0, 10);
const blankCost = () => ({ supplier: '', supplier_id: '', amount: '', stock_items: [] });
const isStockLine = (c) => c.supplier_id === STOCK_SUPPLIER_ID;

/** Log / edit one sale: sale amount ex VAT + any number of supplier cost lines → live profit. */
export function SalesOrderDialog({ open, onClose, onSaved, order = null, showProfit = false }) {
  const [form, setForm] = useState({ date: todayIso(), brand: 'probound', customer: '', description: '', sale_gross: '', vat_rate: 20, notes: '', payment_method: '' });
  const [costs, setCosts] = useState([blankCost()]);
  const [suppliers, setSuppliers] = useState([]);
  const [manage, setManage] = useState(false);
  const [methods, setMethods] = useState([]);
  const [manageMethods, setManageMethods] = useState(false);
  const loadMethods = () => fetchPaymentMethods().then((d) => setMethods(d.methods || [])).catch(() => {});
  const [busy, setBusy] = useState(false);

  const loadSuppliers = () => fetchSuppliers().then((d) => {
    const list = d.suppliers || [];
    setSuppliers(list);
    setCosts((cs) => cs.map((c) => {
      if (c.supplier_id && list.some((s) => s.id === c.supplier_id)) return c;
      const m = c.supplier && list.find((s) => s.name.trim().toLowerCase() === c.supplier.trim().toLowerCase());
      return m ? { ...c, supplier: m.name, supplier_id: m.id } : c;
    }));
  }).catch(() => {});

  useEffect(() => {
    if (!open) return;
    setForm(order
      ? { date: order.date, brand: order.brand, customer: order.customer || '', description: order.description || '', sale_gross: String(order.sale_gross ?? ''), vat_rate: order.vat_rate ?? 20, notes: order.notes || '', payment_method: order.payment_method || '' }
      : { date: todayIso(), brand: 'probound', customer: '', description: '', sale_gross: '', vat_rate: 20, notes: '', payment_method: '' });
    setCosts(order?.costs?.length ? order.costs.map((c) => ({ supplier: c.supplier, supplier_id: c.supplier_id || '', amount: String(c.amount), stock_items: (c.stock_items || []).map((i) => ({ ...i })) })) : [blankCost()]);
    loadSuppliers();
    loadMethods();
  }, [open, order]);

  const saleGross = Number(form.sale_gross || 0);
  const div = 1 + Number(form.vat_rate) / 100;
  const lineAmount = (c) => { if (isStockLine(c)) { const k = stockLineCost(c.stock_items, form.vat_rate); if (k.complete) return k.total; } return c.amount === '' ? '' : Number(c.amount); };
  const costTotal = useMemo(() => costs.reduce((a, c) => a + Number(lineAmount(c) || 0), 0), [costs, form.vat_rate]); // eslint-disable-line react-hooks/exhaustive-deps
  const profit = saleGross - costTotal;
  const profitNet = profit / div;
  const margin = saleGross > 0 ? (profitNet / (saleGross / div)) * 100 : 0;
  const entered = form.sale_gross !== '';
  const lineOk = (c) => (c.supplier_id || c.supplier) && lineAmount(c) !== '' && Number(lineAmount(c)) >= 0 && (!isStockLine(c) || (c.stock_items.length > 0 && c.stock_items.every((i) => Number(i.quantity) >= 1)));
  const lineBlank = (c) => !c.supplier_id && !c.supplier && c.amount === '';
  const validCosts = costs.filter(lineOk);
  const canSave = !busy && form.date && entered && saleGross >= 0 && costs.every((c) => lineBlank(c) || lineOk(c));

  const setCost = (i, patch) => setCosts((cs) => cs.map((c, j) => (j === i ? { ...c, ...patch } : c)));
  const pickSupplier = (i, id) => {
    if (id === '__manage__') { setManage(true); return; }
    if (id === '__legacy__') return;
    const s = suppliers.find((x) => x.id === id);
    if (!s) { setCost(i, { supplier: '', supplier_id: '', stock_items: [] }); return; }
    setCost(i, { supplier: s.name, supplier_id: s.id, stock_items: s.is_stock ? costs[i].stock_items : [], ...(!s.is_stock && s.last_amount != null && !costs[i].amount ? { amount: String(s.last_amount) } : {}) });
  };
  const addLineFor = (s) => setCosts((cs) => { const empty = cs.findIndex(lineBlank); const line = { ...blankCost(), supplier: s.name, supplier_id: s.id }; return empty >= 0 ? cs.map((c, j) => (j === empty ? line : c)) : [...cs, line]; });

  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...form, payment_method: form.payment_method || null, sale_gross: saleGross, vat_rate: Number(form.vat_rate), costs: validCosts.map((c) => ({ supplier: c.supplier, supplier_id: c.supplier_id, amount: Number(lineAmount(c)), stock_items: isStockLine(c) ? c.stock_items.map((i) => ({ product_id: i.product_id, quantity: Number(i.quantity) })) : [] })) };
      const res = await fetch(`${API_URL}/api/staff/sales-orders${order ? `/${order.id}` : ''}`, {
        method: order ? 'PUT' : 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(formatApiError(d) || 'Save failed');
      if (validCosts.some(isStockLine)) clearStockCache();
      const units = validCosts.filter(isStockLine).reduce((a, c) => a + c.stock_items.reduce((b, i) => b + Number(i.quantity), 0), 0);
      toast.success((order ? 'Order updated' : (showProfit && d.profit_net !== undefined ? `Order logged · profit ${gbp(d.profit_net)} ex VAT` : 'Order logged')) + (units ? ` · ${units} unit${units === 1 ? '' : 's'} taken from stock` : ''));
      onSaved(d);
      onClose();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="sales-order-dialog">
        <DialogHeader>
          <DialogTitle>{order ? 'Edit order' : 'Log an order'}</DialogTitle>
          <DialogDescription>Enter the sale and every cost <strong>as paid, including VAT</strong> — straight off the invoices.{showProfit ? ' Profit is worked out with VAT stripped from both.' : ''}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-900 font-semibold" data-testid="sales-order-vat-note">
            All amounts below are <u>including VAT</u> (what actually changed hands).
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <Label htmlFor="so-date">Date</Label>
              <Input id="so-date" type="date" value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} className="bg-white" data-testid="sales-order-date" />
            </div>
            <div className="sm:col-span-2">
              <Label>Brand</Label>
              <div className="flex flex-wrap gap-1.5 mt-1" data-testid="sales-order-brand">
                {SALES_BRANDS.map((b) => (
                  <button key={b.key} type="button" onClick={() => setForm((f) => ({ ...f, brand: b.key }))}
                    className={`px-3 py-1.5 rounded-full text-xs font-bold border-2 transition-colors ${form.brand === b.key ? 'border-transparent' : 'bg-white text-gray-700 border-gray-200 hover:border-gray-300'}`}
                    style={form.brand === b.key ? { backgroundColor: b.color, color: b.ink } : undefined} data-testid={`sales-order-brand-${b.key}`}>{b.label}</button>
                ))}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label htmlFor="so-customer">Customer / job <span className="text-gray-400 font-normal">(optional)</span></Label>
              <Input id="so-customer" value={form.customer} onChange={(e) => setForm((f) => ({ ...f, customer: e.target.value }))} placeholder="e.g. Mr Smith, Leeds driveway" className="bg-white" data-testid="sales-order-customer" />
            </div>
            <div>
              <Label htmlFor="so-desc">What was sold <span className="text-gray-400 font-normal">(optional)</span></Label>
              <Input id="so-desc" value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} placeholder="e.g. 60m² resin bound kit" className="bg-white" data-testid="sales-order-description" />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3">
            <div>
              <Label htmlFor="so-sale">Sale amount (£, inc VAT)</Label>
              <Input id="so-sale" type="number" min="0" step="0.01" value={form.sale_gross} onChange={(e) => setForm((f) => ({ ...f, sale_gross: e.target.value }))} placeholder="Type the sale amount" className="bg-white text-lg font-bold placeholder:text-sm placeholder:font-normal placeholder:text-gray-400" autoFocus data-testid="sales-order-sale" />
              <p className="text-[11px] text-gray-500 mt-1">{entered ? `Ex VAT ${gbp(saleGross / div)} · VAT ${gbp(saleGross - saleGross / div)}` : 'Total the customer paid'}</p>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <Label htmlFor="so-payment">Payment type</Label>
                <button type="button" onClick={() => setManageMethods(true)} className="text-xs font-semibold text-[#1f4030] hover:underline" data-testid="sales-manage-payment-methods">Manage payment types</button>
              </div>
              <select id="so-payment" value={form.payment_method} onChange={(e) => (e.target.value === '__manage__' ? setManageMethods(true) : setForm((f) => ({ ...f, payment_method: e.target.value })))} className="w-full h-10 rounded-md border border-input bg-white px-3 text-sm" data-testid="sales-order-payment-method">
                <option value="">How did they pay?</option>
                {form.payment_method && !methods.some((m) => m.name === form.payment_method) && <option value={form.payment_method}>{form.payment_method} (removed)</option>}
                {methods.map((m) => <option key={m.id} value={m.name}>{m.name}</option>)}
                <option value="__manage__">＋ Add a payment type…</option>
              </select>
              {form.payment_method.toLowerCase() === 'cash' && <p className="text-[11px] text-emerald-800 mt-1" data-testid="sales-order-cash-hint">Paid in cash — this will be recorded in the Cash log automatically.</p>}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1.5">
              <Label>Costs</Label>
              <button type="button" onClick={() => setManage(true)} className="text-xs font-semibold text-[#1f4030] hover:underline" data-testid="sales-manage-suppliers">Manage suppliers</button>
            </div>
            <div className="space-y-2" data-testid="sales-order-costs">
              {costs.map((c, i) => { const auto = isStockLine(c) && stockLineCost(c.stock_items, form.vat_rate).complete; const known = !!c.supplier_id && suppliers.some((s) => s.id === c.supplier_id); const selVal = known ? c.supplier_id : (c.supplier ? '__legacy__' : ''); return (
                <div key={i}>
                  <div className="flex gap-2 items-center">
                    <select value={selVal} onChange={(e) => pickSupplier(i, e.target.value)} className="flex-1 h-10 rounded-md border border-input bg-white px-3 text-sm" data-testid={`sales-cost-supplier-${i}`}>
                      <option value="">{i === 0 ? 'Who was paid?' : 'Supplier'}</option>
                      {c.supplier && !known && <option value="__legacy__">{c.supplier}{suppliers.length ? ' (not in list)' : ''}</option>}
                      {suppliers.map((s) => <option key={s.id} value={s.id}>{s.is_stock ? 'Stock (from the warehouse)' : s.name}</option>)}
                      <option value="__manage__">＋ Add a new supplier…</option>
                    </select>
                    {auto ? (
                      <div className="w-32 h-10 rounded-md border border-emerald-200 bg-emerald-50 px-3 flex flex-col justify-center text-right" title="Worked out from product costs — locked" data-testid={`sales-cost-amount-${i}`}>
                        <span className="text-sm font-bold text-emerald-900 tabular-nums">{gbp(lineAmount(c))}</span>
                        <span className="text-[9px] uppercase tracking-wider text-emerald-700 leading-none">auto</span>
                      </div>
                    ) : (
                      <Input type="number" min="0" step="0.01" value={c.amount} onChange={(e) => setCost(i, { amount: e.target.value })} placeholder={isStockLine(c) ? '£ cost value' : '£'} className="bg-white w-32" data-testid={`sales-cost-amount-${i}`} />
                    )}
                    <button type="button" onClick={() => setCosts((cs) => (cs.length > 1 ? cs.filter((_, j) => j !== i) : [blankCost()]))} className="p-2 text-gray-400 hover:text-rose-600" aria-label="Remove cost" data-testid={`sales-cost-remove-${i}`}><Trash2 className="w-4 h-4" /></button>
                  </div>
                  {isStockLine(c) && <StockPicker items={c.stock_items} vatRate={form.vat_rate} onChange={(items) => setCost(i, { stock_items: items })} testId={`stock-picker-${i}`} />}
                </div>
              ); })}
            </div>
            <Button type="button" variant="outline" size="sm" onClick={() => setCosts((cs) => [...cs, blankCost()])} className="mt-2 h-8" data-testid="sales-cost-add"><Plus className="w-3.5 h-3.5 mr-1" /> Add cost</Button>
            {suppliers.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {suppliers.slice(0, 9).map((s) => (
                  <button key={s.id} type="button" onClick={() => addLineFor(s)}
                    className={`text-[11px] px-2 py-0.5 rounded-full border ${s.is_stock ? 'border-emerald-300 bg-emerald-50 text-emerald-900 font-semibold' : 'border-gray-200 bg-gray-50 hover:border-gray-400'}`} data-testid={`sales-supplier-chip-${s.id}`}>{s.name}</button>
                ))}
              </div>
            )}
          </div>

          <div className={`rounded-lg border bg-gray-50 p-3 grid gap-2 text-center ${showProfit ? 'grid-cols-3' : 'grid-cols-2'}`} data-testid="sales-order-live">
            <div><div className="text-[10px] uppercase tracking-wider text-gray-500">Sale (inc VAT)</div><div className="font-bold text-gray-900">{gbp(saleGross)}</div><div className="text-[11px] text-gray-500">{gbp(saleGross / div)} ex VAT</div></div>
            <div><div className="text-[10px] uppercase tracking-wider text-gray-500">Costs (inc VAT)</div><div className="font-bold text-gray-900">{gbp(costTotal)}</div><div className="text-[11px] text-gray-500">{gbp(costTotal / div)} ex VAT</div></div>
            {showProfit && <div><div className="text-[10px] uppercase tracking-wider text-gray-500">Profit (ex VAT)</div><div className={`text-lg font-bold ${!entered ? 'text-gray-400 text-sm' : profitNet < 0 ? 'text-rose-700' : 'text-emerald-700'}`} data-testid="sales-order-live-profit">{!entered ? '— enter sale' : <>{gbp(profitNet)} <span className="text-xs font-semibold opacity-70">{margin.toFixed(0)}%</span></>}</div></div>}
          </div>

          <div>
            <Label htmlFor="so-notes">Notes <span className="text-gray-400 font-normal">(optional)</span></Label>
            <Textarea id="so-notes" rows={2} value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} className="bg-white" data-testid="sales-order-notes" />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={save} disabled={!canSave} className="bg-[#1f4030] hover:bg-[#16302a] text-white font-bold" data-testid="sales-order-save">
            {busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}{order ? 'Save changes' : 'Log order'}
          </Button>
        </DialogFooter>
      </DialogContent>
      <SupplierManager open={manage} onClose={() => setManage(false)} onChanged={loadSuppliers} />
      <PaymentMethodManager open={manageMethods} onClose={() => setManageMethods(false)} onChanged={loadMethods} />
    </Dialog>
  );
}
